// /weighbridge/reports — Phase C7.
//
// The 19 WB reports register against the central /api/v1/reports surface;
// this page filters the registry to `branch === 'weighbridge'`, renders
// them grouped by category, and reuses the shared report-runner data hooks
// (`useReportsList` / `useRunReport`) + the CSV utilities. The EA waste
// export at `/wb/reports/ea-waste-export` is NOT registry-driven (params
// are `from` / `to`, response is raw CSV) so it lands in its own card.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ArrowRight, Download, FileBarChart2, Play } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import {
  useReportsList,
  useRunReport,
  type ReportInfo,
  type ReportTable,
} from '../useReports';
import {
  buildCsv,
  downloadCsv,
  formatCell,
  startOfMonthIsoDate,
  todayIsoDate,
} from '../reportRunnerUtils';
import { WbCustomerSearch } from '../../weighbridge/components/WbCustomerSearch';
import { useWbYards } from '../../weighbridge/hooks/useWbYards';
import { useWbWeighbridges } from '../../weighbridge/hooks/useWbWeighbridges';
import { useWbBarriers } from '../../weighbridge/hooks/useWbBarriers';
import { useWbProducts } from '../../weighbridge/hooks/useWbProducts';
import { useWbHauliers } from '../../weighbridge/hooks/useWbHauliers';
import { useWbVehicles } from '../../weighbridge/hooks/useWbVehicles';
import styles from './WbReportsPage.module.css';

// -- Schema -----------------------------------------------------------------

type WbReportFilter =
  | 'yard_id'
  | 'weighbridge_id'
  | 'product_id'
  | 'barrier_id'
  | 'customer_id'
  | 'haulier_id'
  | 'vehicle_id'
  | 'direction'
  | 'status'
  | 'payment_method';

interface WbReportSchema {
  dateRange: boolean;
  pagination: boolean;
  filters: WbReportFilter[];
  /** When `status` is present and the report's allowed values aren't the default. */
  statusOptions?: ReadonlyArray<{ value: string; label: string }>;
}

// Per-report params from WEIGHBRIDGE_SERVER_CONTRACT.md §C7.
const REPORT_SCHEMAS: Record<string, WbReportSchema> = {
  // Operational
  wb_daily_ticket_summary: {
    dateRange: true,
    pagination: false,
    filters: ['yard_id', 'weighbridge_id'],
  },
  wb_stock_movement: {
    dateRange: true,
    pagination: true,
    filters: ['product_id', 'yard_id', 'direction'],
  },
  wb_stock_threshold_breaches: {
    dateRange: true,
    pagination: true,
    filters: ['status', 'product_id', 'yard_id'],
    statusOptions: [
      { value: 'active', label: 'Active' },
      { value: 'dismissed', label: 'Dismissed' },
      { value: 'auto_resolved', label: 'Auto-resolved' },
    ],
  },
  wb_open_tickets_aging: {
    dateRange: false,
    pagination: false,
    filters: ['yard_id', 'status'],
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'open', label: 'Open' },
    ],
  },
  // Party
  wb_customer_activity: {
    dateRange: true,
    pagination: false,
    filters: ['customer_id'],
  },
  wb_vehicle_activity: {
    dateRange: true,
    pagination: false,
    filters: ['haulier_id'],
  },
  wb_driver_activity: { dateRange: true, pagination: false, filters: [] },
  wb_haulier_activity: { dateRange: true, pagination: false, filters: [] },
  wb_bridge_utilisation: {
    dateRange: true,
    pagination: false,
    filters: ['weighbridge_id'],
  },
  wb_operator_performance: { dateRange: true, pagination: false, filters: [] },
  // Analysis
  wb_product_performance: {
    dateRange: true,
    pagination: false,
    filters: ['product_id'],
  },
  wb_manual_override_audit: { dateRange: true, pagination: false, filters: [] },
  wb_calibration_status: { dateRange: false, pagination: false, filters: [] },
  wb_trade_approval_breaches: {
    dateRange: true,
    pagination: false,
    filters: [],
  },
  wb_barrier_event_log: {
    dateRange: true,
    pagination: false,
    filters: ['barrier_id'],
  },
  // Compliance / Financial
  wb_wtn_log: { dateRange: true, pagination: false, filters: [] },
  wb_hazardous_consignment_log: {
    dateRange: true,
    pagination: false,
    filters: [],
  },
  wb_vat_report: { dateRange: true, pagination: false, filters: [] },
  wb_sales_day_book: {
    dateRange: true,
    pagination: false,
    filters: ['payment_method'],
  },
};

interface ReportCategory {
  label: string;
  keys: ReadonlyArray<string>;
}

const CATEGORIES: ReadonlyArray<ReportCategory> = [
  {
    label: 'Operational',
    keys: [
      'wb_daily_ticket_summary',
      'wb_stock_movement',
      'wb_stock_threshold_breaches',
      'wb_open_tickets_aging',
    ],
  },
  {
    label: 'Party',
    keys: [
      'wb_customer_activity',
      'wb_vehicle_activity',
      'wb_driver_activity',
      'wb_haulier_activity',
      'wb_bridge_utilisation',
      'wb_operator_performance',
    ],
  },
  {
    label: 'Analysis',
    keys: [
      'wb_product_performance',
      'wb_manual_override_audit',
      'wb_calibration_status',
      'wb_trade_approval_breaches',
      'wb_barrier_event_log',
    ],
  },
  {
    label: 'Compliance & Financial',
    keys: [
      'wb_wtn_log',
      'wb_hazardous_consignment_log',
      'wb_vat_report',
      'wb_sales_day_book',
    ],
  },
];

const EA_EXPORT_KEY = '__ea_waste_export';

const DIRECTION_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'inwards', label: 'Inwards' },
  { value: 'outwards', label: 'Outwards' },
];

const PAYMENT_METHOD_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'account', label: 'Account' },
  { value: 'mixed', label: 'Mixed' },
];

// -- Component --------------------------------------------------------------

interface FormState {
  date_from: string;
  date_to: string;
  yard_id: string;
  weighbridge_id: string;
  product_id: string;
  barrier_id: string;
  customer_id: string;
  haulier_id: string;
  vehicle_id: string;
  direction: string;
  status: string;
  payment_method: string;
  page: string;
  per_page: string;
}

function emptyForm(): FormState {
  return {
    date_from: startOfMonthIsoDate(),
    date_to: todayIsoDate(),
    yard_id: '',
    weighbridge_id: '',
    product_id: '',
    barrier_id: '',
    customer_id: '',
    haulier_id: '',
    vehicle_id: '',
    direction: '',
    status: '',
    payment_method: '',
    page: '',
    per_page: '',
  };
}

export function WbReportsPage(): JSX.Element {
  const listQuery = useReportsList();
  const allReports: ReportInfo[] = listQuery.data ?? [];
  const wbReports = useMemo(
    () => allReports.filter((r) => r.branch === 'weighbridge'),
    [allReports],
  );
  const wbReportByKey = useMemo(() => {
    const map = new Map<string, ReportInfo>();
    for (const r of wbReports) map.set(r.key, r);
    return map;
  }, [wbReports]);

  const [selectedKey, setSelectedKey] = useState<string>('');
  const [form, setForm] = useState<FormState>(emptyForm());
  const [submitted, setSubmitted] = useState<Record<string, string> | null>(
    null,
  );

  // Reset the form whenever the user picks a different report.
  useEffect(() => {
    setForm(emptyForm());
    setSubmitted(null);
  }, [selectedKey]);

  const schema = selectedKey ? REPORT_SCHEMAS[selectedKey] : undefined;
  const meta = selectedKey ? wbReportByKey.get(selectedKey) : undefined;

  const runQuery = useRunReport(
    selectedKey,
    submitted ?? {},
    !!selectedKey && selectedKey !== EA_EXPORT_KEY && !!submitted,
  );
  const result = runQuery.data;

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleRun(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!schema) return;
    const params: Record<string, string> = {};
    if (schema.dateRange) {
      params['date_from'] = form.date_from;
      params['date_to'] = form.date_to;
    }
    for (const filter of schema.filters) {
      const value = form[filter];
      if (value !== '') params[filter] = value;
    }
    if (schema.pagination) {
      if (form.page !== '') params['page'] = form.page;
      if (form.per_page !== '') params['per_page'] = form.per_page;
    }
    setSubmitted(params);
  }

  function handleExport() {
    if (!result) return;
    const csv = buildCsv(result);
    downloadCsv(`${selectedKey}-${todayIsoDate()}.csv`, csv);
  }

  const errorMsg =
    runQuery.error instanceof Error ? runQuery.error.message : null;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Weighbridge Reports"
        subtitle={`${wbReports.length || 19} reports plus the EA waste export.`}
      />

      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          {listQuery.isLoading ? (
            <Card>
              <div className={styles.empty}>
                <Spinner />
              </div>
            </Card>
          ) : (
            <ReportSidebar
              reports={wbReports}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
            />
          )}
        </aside>

        <section className={styles.main}>
          {!selectedKey ? (
            <Card>
              <div className={styles.empty}>
                <FileBarChart2 size={32} aria-hidden />
                <p>Pick a report on the left to begin.</p>
              </div>
            </Card>
          ) : selectedKey === EA_EXPORT_KEY ? (
            <EaWasteExportCard />
          ) : (
            <>
              <Card title={meta?.title ?? selectedKey}>
                {meta?.description ? (
                  <p className={styles.helper}>{meta.description}</p>
                ) : null}
                <form onSubmit={handleRun} className={styles.form}>
                  {schema?.dateRange ? (
                    <div className={styles.row}>
                      <label className={styles.dateField}>
                        <span>Date from</span>
                        <input
                          type="date"
                          value={form.date_from}
                          onChange={(e) =>
                            setField('date_from', e.target.value)
                          }
                          required
                        />
                      </label>
                      <label className={styles.dateField}>
                        <span>Date to</span>
                        <input
                          type="date"
                          value={form.date_to}
                          onChange={(e) => setField('date_to', e.target.value)}
                          required
                        />
                      </label>
                    </div>
                  ) : null}

                  {schema?.filters.length ? (
                    <div className={styles.filtersGrid}>
                      {schema.filters.map((filter) => (
                        <FilterControl
                          key={filter}
                          filter={filter}
                          value={form[filter]}
                          onChange={(value) => setField(filter, value)}
                          statusOptions={schema.statusOptions}
                        />
                      ))}
                    </div>
                  ) : null}

                  {schema?.pagination ? (
                    <div className={styles.row}>
                      <label className={styles.dateField}>
                        <span>Page</span>
                        <input
                          type="number"
                          min={1}
                          value={form.page}
                          onChange={(e) => setField('page', e.target.value)}
                          placeholder="1"
                        />
                      </label>
                      <label className={styles.dateField}>
                        <span>Per page</span>
                        <input
                          type="number"
                          min={1}
                          max={500}
                          value={form.per_page}
                          onChange={(e) =>
                            setField('per_page', e.target.value)
                          }
                          placeholder="50"
                        />
                      </label>
                    </div>
                  ) : null}

                  <div className={styles.formActions}>
                    <Button
                      type="submit"
                      variant="primary"
                      icon={Play}
                      loading={runQuery.isFetching}
                    >
                      Run report
                    </Button>
                    {result ? (
                      <Button
                        type="button"
                        variant="secondary"
                        icon={Download}
                        onClick={handleExport}
                      >
                        Export CSV
                      </Button>
                    ) : null}
                  </div>
                </form>
              </Card>

              {runQuery.isError ? (
                <Card>
                  <p className={styles.errorMsg}>
                    {errorMsg ?? 'Failed to run report.'}
                  </p>
                </Card>
              ) : null}

              {runQuery.isFetching && !result ? (
                <Card>
                  <div className={styles.empty}>
                    <Spinner />
                  </div>
                </Card>
              ) : null}

              {result ? <ReportTableView table={result} /> : null}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

// -- Sub-components ---------------------------------------------------------

interface ReportSidebarProps {
  reports: ReportInfo[];
  selectedKey: string;
  onSelect: (key: string) => void;
}

function ReportSidebar({
  reports,
  selectedKey,
  onSelect,
}: ReportSidebarProps): JSX.Element {
  const byKey = useMemo(() => {
    const map = new Map<string, ReportInfo>();
    for (const r of reports) map.set(r.key, r);
    return map;
  }, [reports]);

  return (
    <Card flush>
      <nav className={styles.nav} aria-label="Reports">
        {CATEGORIES.map((cat) => (
          <div key={cat.label} className={styles.navGroup}>
            <h4 className={styles.navHeading}>{cat.label}</h4>
            <ul className={styles.navList}>
              {cat.keys.map((key) => {
                const info = byKey.get(key);
                if (!info) return null;
                const active = key === selectedKey;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      className={
                        active
                          ? `${styles.navItem} ${styles.navItemActive}`
                          : styles.navItem
                      }
                      onClick={() => onSelect(key)}
                    >
                      <span className={styles.navItemTitle}>{info.title}</span>
                      <ArrowRight size={14} aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        <div className={styles.navGroup}>
          <h4 className={styles.navHeading}>Compliance · Export</h4>
          <ul className={styles.navList}>
            <li>
              <button
                type="button"
                className={
                  selectedKey === EA_EXPORT_KEY
                    ? `${styles.navItem} ${styles.navItemActive}`
                    : styles.navItem
                }
                onClick={() => onSelect(EA_EXPORT_KEY)}
              >
                <span className={styles.navItemTitle}>EA Waste Export</span>
                <ArrowRight size={14} aria-hidden />
              </button>
            </li>
          </ul>
        </div>
      </nav>
    </Card>
  );
}

interface FilterControlProps {
  filter: WbReportFilter;
  value: string;
  onChange: (value: string) => void;
  statusOptions?: ReadonlyArray<{ value: string; label: string }>;
}

function FilterControl({
  filter,
  value,
  onChange,
  statusOptions,
}: FilterControlProps): JSX.Element {
  switch (filter) {
    case 'yard_id':
      return (
        <YardSelect label="Yard" value={value} onChange={onChange} />
      );
    case 'weighbridge_id':
      return (
        <WeighbridgeSelect label="Weighbridge" value={value} onChange={onChange} />
      );
    case 'product_id':
      return (
        <ProductSelect label="Product" value={value} onChange={onChange} />
      );
    case 'barrier_id':
      return (
        <BarrierSelect label="Barrier" value={value} onChange={onChange} />
      );
    case 'haulier_id':
      return (
        <HaulierSelect label="Haulier" value={value} onChange={onChange} />
      );
    case 'vehicle_id':
      return (
        <VehicleSelect label="Vehicle" value={value} onChange={onChange} />
      );
    case 'customer_id':
      return (
        <label className={styles.filterField}>
          <span>Customer</span>
          <WbCustomerSearch
            value={value === '' ? null : value}
            onChange={(encId) => onChange(encId ?? '')}
            placeholder="Any customer"
          />
        </label>
      );
    case 'direction':
      return (
        <NativeSelect
          label="Direction"
          value={value}
          onChange={onChange}
          options={DIRECTION_OPTIONS}
        />
      );
    case 'status':
      return (
        <NativeSelect
          label="Status"
          value={value}
          onChange={onChange}
          options={statusOptions ?? []}
        />
      );
    case 'payment_method':
      return (
        <NativeSelect
          label="Payment method"
          value={value}
          onChange={onChange}
          options={PAYMENT_METHOD_OPTIONS}
        />
      );
    default:
      return <></>;
  }
}

interface NativeSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}

function NativeSelect({
  label,
  value,
  onChange,
  options,
}: NativeSelectProps): JSX.Element {
  return (
    <label className={styles.filterField}>
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Any</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function YardSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  const q = useWbYards();
  return (
    <label className={styles.filterField}>
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Any</option>
        {(q.data?.yards ?? []).map((y) => (
          <option key={y.enc_id} value={y.enc_id}>
            {y.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function WeighbridgeSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  const q = useWbWeighbridges();
  const list =
    (q.data as { weighbridges?: { enc_id: string; name: string }[] } | undefined)
      ?.weighbridges ?? [];
  return (
    <label className={styles.filterField}>
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Any</option>
        {list.map((w) => (
          <option key={w.enc_id} value={w.enc_id}>
            {w.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function ProductSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  const q = useWbProducts({ per_page: 100, is_active: true });
  return (
    <label className={styles.filterField}>
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Any</option>
        {(q.data?.products ?? []).map((p) => (
          <option key={p.enc_id} value={p.enc_id}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function BarrierSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  const q = useWbBarriers();
  const list = (q.data?.barriers ?? []) as ReadonlyArray<{
    enc_id: string;
    name: string;
  }>;
  return (
    <label className={styles.filterField}>
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Any</option>
        {list.map((b) => (
          <option key={b.enc_id} value={b.enc_id}>
            {b.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function HaulierSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  const q = useWbHauliers({ per_page: 100, is_active: true });
  return (
    <label className={styles.filterField}>
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Any</option>
        {(q.data?.hauliers ?? []).map((h) => (
          <option key={h.enc_id} value={h.enc_id}>
            {h.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function VehicleSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): JSX.Element {
  const q = useWbVehicles({ per_page: 100, is_active: true });
  return (
    <label className={styles.filterField}>
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Any</option>
        {(q.data?.vehicles ?? []).map((v) => (
          <option key={v.enc_id} value={v.enc_id}>
            {v.registration}
          </option>
        ))}
      </select>
    </label>
  );
}

// -- Results table ----------------------------------------------------------

function ReportTableView({ table }: { table: ReportTable }): JSX.Element {
  if (table.rows.length === 0) {
    return (
      <Card title="Results">
        <p className={styles.empty}>No rows match the selected parameters.</p>
        {table.meta ? <Meta meta={table.meta} /> : null}
      </Card>
    );
  }
  return (
    <Card title={`Results (${table.rows.length} rows)`} flush>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              {table.columns.map((c) => (
                <th key={c.key} className={styles[`col_${c.type}`] ?? ''}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, idx) => (
              <tr key={idx}>
                {table.columns.map((c) => (
                  <td key={c.key} className={styles[`col_${c.type}`] ?? ''}>
                    {formatCell(row[c.key] ?? null, c.type)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.meta ? <Meta meta={table.meta} /> : null}
    </Card>
  );
}

function Meta({
  meta,
}: {
  meta: Record<string, string | number | null>;
}): JSX.Element {
  return (
    <ul className={styles.meta}>
      {Object.entries(meta).map(([k, v]) => (
        <li key={k}>
          <span className={styles.metaKey}>{k.replace(/_/g, ' ')}</span>
          <span className={styles.metaValue}>
            {v === null ? '—' : String(v)}
          </span>
        </li>
      ))}
    </ul>
  );
}

// -- EA Waste Export --------------------------------------------------------

function EaWasteExportCard(): JSX.Element {
  const [from, setFrom] = useState<string>(startOfMonthIsoDate());
  const [to, setTo] = useState<string>(todayIsoDate());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDownload(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // The endpoint requires staff auth + module access. apiClient.raw
      // is the underlying axios instance and inherits the request
      // interceptor that attaches the Bearer token automatically.
      const url = `${endpoints.wb.eaExport}?from=${encodeURIComponent(
        from,
      )}&to=${encodeURIComponent(to)}`;
      const res = await apiClient.raw.get(url, { responseType: 'blob' });
      const blob = res.data as Blob;
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `ea-waste-export-${from}-to-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="EA Waste Export">
      <p className={styles.helper}>
        Environment Agency CSV export for hazardous and non-hazardous waste
        movements. Downloads directly — not part of the standard report
        registry.
      </p>
      <form onSubmit={handleDownload} className={styles.form}>
        <div className={styles.row}>
          <label className={styles.dateField}>
            <span>From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              required
            />
          </label>
          <label className={styles.dateField}>
            <span>To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              required
            />
          </label>
        </div>
        <div className={styles.formActions}>
          <Button
            type="submit"
            variant="primary"
            icon={Download}
            loading={busy}
          >
            Download CSV
          </Button>
        </div>
        {error ? <p className={styles.errorMsg}>{error}</p> : null}
      </form>
    </Card>
  );
}
